import React from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import themeSchema from '../../../lib/composer/schemas/theme';
import { ctaSchema, ctasSchema } from '../../../lib/composer/schemas/cta';
import textSchema from '../../../lib/composer/schemas/text';
import subheadingSchema, {
  subheadingAlignmentSchema,
  subheadingFontSizeSchema,
  subheadingTextColorSchema,
} from '../../../lib/composer/schemas/subheading';
import headingSchema, {
  headingAlignmentSchema,
  headingFontSizeSchema,
  headingTextColorSchema,
} from '../../../lib/composer/schemas/heading';
import mediaSchema from '../../../lib/composer/schemas/media';
import CTAs from '../block-ui/CTAs';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import Media from '../block-ui/Media';
import RichText from '../block-ui/RichText';
import { MediaBannerBlockProps } from '../../../types/composer';
import clsx from 'clsx';
import overrideBackgroundSchema from '../../../lib/composer/schemas/backgroundColor';
import { fontStyleSchema } from '../../../lib/composer/schemas/font';
import wordPressSchema from '../../../lib/composer/schemas/wordPress';

/**
 * Media banner block.
 */
const MediaBanner = (props: MediaBannerBlockProps) => {
  const {
    heading,
    headingAlignment = 'center',
    headingTextColor = 'text-neutral-700',
    headingFontSize = 'default',
    subheading,
    subheadingAlignment = 'center',
    subheadingTextColor = 'text-neutral-700',
    subheadingFontSize = 'default',
    cta: ctaProps,
    ctas: ctasProps,
    text,
    theme = '',
    media,
    topHeader,
    fontStyle = 'default',
    overrideBackground = 'default',
    isWordPressBlock = false,
  } = props;

  const ctas = [...(ctaProps?.caption ? [ctaProps] : []), ...(ctasProps || [])];

  media.alignment = 'center';

  return (
    <Flex
      className={clsx(
        overrideBackground !== 'default' ? overrideBackground : `bg-${theme}`,
        fontStyle && fontStyle !== 'default' && fontStyle,
        'justify-center',
      )}
      data-testid="media-banner-block"
    >
      <Flex className={`w-2/3 flex-col items-center justify-center px-4 py-5`}>
        <Flex className={`mx-3 flex-1 flex-col items-center gap-2`}>
          <Heading
            text={heading}
            alignment={headingAlignment}
            textColor={headingTextColor}
            theme={theme}
            topHeader={topHeader}
            fontSize={headingFontSize}
            isWordPressBlock={isWordPressBlock}
          />

          {subheading && (
            <Subheading
              text={subheading}
              alignment={subheadingAlignment}
              textColor={subheadingTextColor}
              theme={theme}
              topHeader={topHeader}
              fontSize={subheadingFontSize}
              isWordPressBlock={isWordPressBlock}
            />
          )}

          {text && <RichText theme={theme} html={text} />}
          <CTAs ctas={ctas} theme={theme} />
        </Flex>

        {media.src && (
          <Box className={`mx-3 flex-1`}>
            <Media media={media} />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

MediaBanner.schema = {
  name: 'media-banner',
  label: 'Media banner',
  editableProps: [
    headingSchema,
    headingAlignmentSchema,
    headingTextColorSchema,
    headingFontSizeSchema,
    subheadingSchema,
    subheadingAlignmentSchema,
    subheadingTextColorSchema,
    subheadingFontSizeSchema,
    textSchema,
    {
      ...mediaSchema,
      properties: {
        ...mediaSchema.properties,
        alignment: {
          ...mediaSchema.properties.alignment,
        },
      },
    },
    ctaSchema,
    ctasSchema,
    themeSchema,
    wordPressSchema,
    fontStyleSchema,
    overrideBackgroundSchema,
  ],
};

export default MediaBanner;
